/**
 * /ndt-inspection/tickets/:id — one NDT field ticket on its own page
 * (GET /ndt-inspection/tickets/:id, fieldiq-api#1157). The back arrow returns
 * to the register with its filters, or to the job that opened it.
 *
 * EVERY FIGURE IS THE API'S. Each line's amount and `money` verdict, and the
 * subtotal, are read as returned. "No rate" (with the API's reason — a tie
 * names both codes) and "No charge" are words, never $0. Nothing here
 * multiplies or adds.
 *
 * WHAT CAN BE DONE follows the status the API sent:
 *   draft     add a line · change a quantity · (office) set a price, pick the
 *             rate line for an unpriced one, Reprice · remove a line · Void
 *   signed    Approve · Void
 *   approved  Void
 *   invoiced, void   nothing but the PDF
 * Signing is the company man's, on the crew's phone (/field/ndt/tickets/:id).
 * Approve and Void are NDT_DISPATCH; a button a role cannot press is left off.
 * Void asks for its reason inline, never through window.confirm.
 *
 * PREFLIGHT (batch 6, fieldiq-api#1178) — office roles that review AI drafts
 * (admin, office_staff, supervisor) on any ticket but a void one. It lists the
 * API's unpriced lines and the exams no ticket bills, then the AI's draft
 * explanation, which is reviewed like every other AI digest.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Download, ListChecks, Loader2, Pencil, Plus, RefreshCw, Tag, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api, getBlobApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useApproveNdtTicket, useNdtTicket, useRepriceNdtTicket, useVoidNdtTicket } from '../../hooks/useNdtTickets';
import { useNdtTicketInvoice } from '../../hooks/useNdtInvoices';
import { useRunNdtAi } from '../../hooks/useNdtAi';
import { canUseNdtAi } from '../../lib/ndt-ai';
import type { NdtAiRunResult, NdtPreflight } from '../../types/ndt-ai';
import { canReadNdtInvoices } from '../../lib/ndt-invoices';
import { saveBlobAs } from '../../lib/blob-tab';
import { formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { ndtLocationRefLabel } from '../../lib/ndt-inspection';
import {
  canPriceNdtTickets,
  canWorkNdtTickets,
  ndtLineAmountText,
  ndtLineNeedsPrice,
  ndtLineUnitLabel,
  ndtQtyText,
  ndtRateText,
  ndtSubtotalText,
  ndtTicketPdfFilename,
  ndtTicketRefusal,
  ndtTicketScopeText,
  ndtTicketStatusSkin,
} from '../../lib/ndt-tickets';
import type { NdtTicketLine } from '../../types/ndt-tickets';
import { useBackToList } from '../downhole/recordPage';
import { NDT_INPUT, NDT_SECONDARY, NdtBadge, NdtDialog, NdtField, NdtFormError, NdtLoading, NdtPanel, NdtSpec, isNotFound, ndtDay } from './NdtBits';
import { NdtAddLineForm, NdtEditLineForm, NdtPickRateForm, NdtRemoveLineForm } from './NdtTicketBits';
import { TicketPreflightResult } from './NdtAiBits';

const LIST = '/ndt-inspection/tickets';
const BAND_SECONDARY =
  'inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60';

type LineDialog = { kind: 'add' } | { kind: 'edit'; line: NdtTicketLine } | { kind: 'pick'; line: NdtTicketLine } | { kind: 'remove'; line: NdtTicketLine };

function rateSourceText(l: NdtTicketLine): string {
  if (l.rateSource === 'office') return 'Set by the office';
  if (l.rateSource === 'rate_card') return l.rateCode ? `Rate card ${l.rateCode}` : 'Rate card';
  return '';
}

export default function TicketDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList(LIST);
  const { user } = useAuth();
  const query = useNdtTicket(id);
  const ticket = query.data?.data ?? null;
  const meta = query.data?.meta;
  const tz = meta?.timeZone ?? null;
  const reprice = useRepriceNdtTicket();
  const approve = useApproveNdtTicket();
  const voidTicket = useVoidNdtTicket();
  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidError, setVoidError] = useState<string | null>(null);
  const [lineDialog, setLineDialog] = useState<LineDialog | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const preflight = useRunNdtAi();
  const [preflightResult, setPreflightResult] = useState<NdtAiRunResult<NdtPreflight> | null>(null);
  const canPreflight = canUseNdtAi(user?.role);
  const failure = queryLoadFailure(query);
  const onInvoice = useNdtTicketInvoice(ticket?.id, ticket?.jobId, ticket?.status === 'invoiced' && canReadNdtInvoices(user?.role));
  const notFound = !ticket && !query.isLoading && isNotFound(queryFailureError(query));

  const canPrice = canPriceNdtTickets(user?.role);
  const canWork = canWorkNdtTickets(user?.role);
  const status = ticket?.status ?? null;
  const isDraft = status === 'draft';

  useEffect(() => setActionError(null), [query.dataUpdatedAt]);

  const downloadPdf = async () => {
    if (!ticket) return;
    setActionError(null);
    setPdfBusy(true);
    try {
      const res = await api.ndtInspection.tickets.pdf(ticket.id);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart], { type: 'application/pdf' });
      saveBlobAs(blob, ndtTicketPdfFilename(ticket));
    } catch (err) {
      setActionError(await getBlobApiErrorMessage(err, 'The PDF could not be loaded.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const onReprice = () => {
    if (!ticket) return;
    setActionError(null);
    reprice.mutate(ticket.id, {
      onSuccess: () => toast.success(`${ticket.ticketNumber} repriced against the rate card`),
      onError: (err) => setActionError(ndtTicketRefusal(err, 'The ticket was not repriced.').message),
    });
  };

  const onPreflight = () => {
    if (!ticket) return;
    setActionError(null);
    preflight.mutate(
      { agent: 'ndt_ticket_preflight', ticketId: ticket.id },
      {
        onSuccess: (r) => setPreflightResult((r as NdtAiRunResult<NdtPreflight> | null) ?? null),
        onError: (err) => setActionError(ndtTicketRefusal(err, 'The preflight did not run.').message),
      },
    );
  };

  const onApprove = () => {
    if (!ticket) return;
    setActionError(null);
    approve.mutate(ticket.id, {
      onSuccess: () => toast.success(`${ticket.ticketNumber} approved`),
      onError: (err) => setActionError(ndtTicketRefusal(err, 'The ticket was not approved.').message),
    });
  };

  const onVoid = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticket) return;
    setVoidError(null);
    if (!voidReason.trim()) return setVoidError('A reason is required to void a ticket.');
    voidTicket.mutate(
      { id: ticket.id, reason: voidReason.trim() },
      {
        onSuccess: () => {
          toast.success(`${ticket.ticketNumber} voided`);
          setVoiding(false);
          setVoidReason('');
        },
        onError: (err) => setVoidError(ndtTicketRefusal(err, 'The ticket was not voided.').message),
      },
    );
  };

  const lines = ticket?.lines ?? [];
  const removed = ticket?.removedLines ?? [];
  const unpriced = lines.filter(ndtLineNeedsPrice);
  const day = ticket?.workDate ?? meta?.today ?? null;

  const actions = ticket ? (
    <div className="flex flex-wrap gap-2" data-testid="ndt-ticket-actions">
      <button type="button" className={BAND_SECONDARY} onClick={downloadPdf} disabled={pdfBusy} data-testid="ndt-ticket-pdf">
        {pdfBusy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Download className="size-4" aria-hidden="true" />}
        PDF
      </button>
      {canPreflight && status !== 'void' && (
        <button type="button" className={BAND_SECONDARY} onClick={onPreflight} disabled={preflight.isPending} data-testid="ndt-ticket-preflight">
          {preflight.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <ListChecks className="size-4" aria-hidden="true" />}
          {preflight.isPending ? 'Checking…' : 'Preflight'}
        </button>
      )}
      {canPrice && isDraft && (
        <button type="button" className={BAND_SECONDARY} onClick={onReprice} disabled={reprice.isPending} data-testid="ndt-ticket-reprice">
          {reprice.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <RefreshCw className="size-4" aria-hidden="true" />}
          Reprice
        </button>
      )}
      {canPrice && status !== 'void' && status !== 'invoiced' && (
        <button type="button" className={BAND_SECONDARY} onClick={() => setVoiding(true)} data-testid="ndt-ticket-void">
          <XCircle className="size-4" aria-hidden="true" />
          Void
        </button>
      )}
      {canPrice && status === 'signed' && (
        <button type="button" className="fi-action fi-action--standard fi-action--primary" onClick={onApprove} disabled={approve.isPending} data-testid="ndt-ticket-approve">
          {approve.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
          Approve
        </button>
      )}
    </div>
  ) : undefined;

  return (
    <div data-testid="ndt-ticket-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT field ticket"
        title={ticket ? ticket.ticketNumber || 'Field ticket' : query.isLoading ? 'Loading…' : 'Field ticket'}
        onBack={back}
        backLabel="Back to field tickets"
        actions={actions}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="ndt-ticket-detail">
          {!ticket && query.isLoading ? (
            <NdtLoading label="Loading the field ticket" />
          ) : notFound ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="ndt-ticket-not-found">
              This field ticket was not found.
            </p>
          ) : !ticket ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? undefined} subject="this field ticket" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <NdtBadge skin={ndtTicketStatusSkin(status)} value={status} />
                <Link to={`/ndt-inspection/jobs/${ticket.jobId}`} className="text-sm font-bold text-fieldiq-text underline">
                  {ticket.jobNumber || '—'}
                </Link>
                <span className="text-sm text-fieldiq-muted">{ticket.customerName || '—'}</span>
              </div>
              {failure && (
                <p role="status" className="text-xs text-fieldiq-muted">
                  This ticket could not be refreshed just now, so it shows what loaded earlier.
                </p>
              )}

              {voiding && (
                <form onSubmit={onVoid} className="space-y-3 rounded-fi-panel border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3" data-testid="ndt-ticket-void-form" noValidate>
                  <p className="text-sm font-bold text-fieldiq-text">Void {ticket.ticketNumber}?</p>
                  <p className="text-sm text-fieldiq-text">A void ticket is kept for the record and cannot be billed. Its exams are freed for a new ticket.</p>
                  <NdtFormError message={voidError} />
                  <NdtField label="Reason">
                    <textarea className={`${NDT_INPUT} h-20 py-2`} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} maxLength={1000} data-testid="ndt-ticket-void-reason" autoFocus />
                  </NdtField>
                  <div className="flex justify-end gap-2">
                    <button type="button" className={NDT_SECONDARY} onClick={() => setVoiding(false)} disabled={voidTicket.isPending}>
                      Keep ticket
                    </button>
                    <button
                      type="submit"
                      disabled={voidTicket.isPending}
                      data-testid="ndt-ticket-void-confirm"
                      className="inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {voidTicket.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
                      Void ticket
                    </button>
                  </div>
                </form>
              )}

              {status === 'void' && (
                <div role="status" data-testid="ndt-ticket-void-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-steel bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-bold">Void {formatInstant(ticket.voidedAt, tz) ?? ''}</p>
                  <p className="mt-1 whitespace-pre-wrap">{ticket.voidReason || '—'}</p>
                </div>
              )}
              {ticket.signature && (
                <p data-testid="ndt-ticket-signed-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-blue bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  Signed {formatInstant(ticket.signature.signedAt, tz) ?? '—'} by {ticket.signature.signerName || '—'}
                  {ticket.signature.signerTitle ? `, ${ticket.signature.signerTitle}` : ''}.
                  {status === 'signed' || status === 'approved' ? ' A signed ticket cannot change; void it and draft a new one to correct it.' : ''}
                </p>
              )}
              {ticket.approvedAt && (
                <p data-testid="ndt-ticket-approved-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  Approved {formatInstant(ticket.approvedAt, tz) ?? '—'} by {ticket.approvedByName || '—'}.
                </p>
              )}
              {status === 'invoiced' && (
                <p data-testid="ndt-ticket-invoice-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  {onInvoice.data ? (
                    <>
                      On invoice{' '}
                      <Link to={`/ndt-inspection/invoices/${onInvoice.data.id}`} className="font-mono font-bold underline" data-testid="ndt-ticket-invoice-link">
                        {onInvoice.data.invoiceNumber}
                      </Link>
                      .
                    </>
                  ) : !canReadNdtInvoices(user?.role) ? (
                    'Invoiced.'
                  ) : onInvoice.isLoading ? (
                    'Invoiced. Finding the invoice…'
                  ) : (
                    'Invoiced. The invoice could not be found from here; see Finance → Invoices.'
                  )}
                </p>
              )}
              {actionError && (
                <p role="alert" data-testid="ndt-ticket-action-error" className="rounded-fi-panel border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text">
                  {actionError}
                </p>
              )}

              {preflightResult && preflightResult.data?.ticket?.id === ticket.id && (
                <TicketPreflightResult result={preflightResult} canReview={canPreflight} />
              )}

              <NdtPanel eyebrow="Ticket" title="Details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-3">
                  <NdtSpec label="Covers" value={ndtTicketScopeText(ticket.scope, ticket.workDate)} />
                  <NdtSpec label="Work date" value={ndtDay(ticket.workDate)} />
                  <NdtSpec label="Customer" value={ticket.customerName || '—'} />
                  <NdtSpec label="Location" value={ticket.locationName || '—'} />
                  <NdtSpec label={ndtLocationRefLabel(ticket.provinceState, ticket.currency)} value={ticket.lsd || '—'} mono />
                  <NdtSpec label="Line / project" value={[ticket.lineRef, ticket.projectRef].filter(Boolean).join(' · ') || '—'} />
                  <NdtSpec label="PO" value={ticket.poNumber || '—'} mono />
                  <NdtSpec label="AFE" value={ticket.afeNumber || '—'} mono />
                  <NdtSpec label="Cost code" value={ticket.costCode || '—'} mono />
                  <NdtSpec label="Signer" value={ticket.signature ? [ticket.signature.signerName, ticket.signature.signerTitle].filter(Boolean).join(', ') || '—' : 'Not signed'} />
                  <NdtSpec label="Currency" value={ticket.currency || '—'} />
                  <NdtSpec label="Created" value={formatInstant(ticket.createdAt, tz) ?? '—'} />
                </dl>
              </NdtPanel>

              {isDraft && unpriced.length > 0 && (
                <div role="status" data-testid="ndt-ticket-unpriced" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="flex items-center gap-2 font-bold">
                    <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
                    {unpriced.length} line{unpriced.length === 1 ? ' has' : 's have'} no price, so the company man cannot sign yet.
                  </p>
                  <p className="mt-1 text-xs">The office picks the rate line, sets a price (0 is no charge), or removes the line.</p>
                </div>
              )}

              <NdtPanel
                eyebrow="Charges"
                title="Lines"
                testId="ndt-ticket-lines"
                actions={
                  canWork && isDraft ? (
                    <button type="button" className={NDT_SECONDARY} onClick={() => setLineDialog({ kind: 'add' })} data-testid="ndt-ticket-add-line">
                      <Plus className="size-4" aria-hidden="true" />
                      Add line
                    </button>
                  ) : undefined
                }
              >
                {lines.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-fieldiq-text">This ticket has no charges.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[880px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr className="border-l-4 border-l-transparent">
                          <th scope="col" className="px-4 py-3">Charge</th>
                          <th scope="col" className="px-4 py-3 text-right">Qty</th>
                          <th scope="col" className="px-4 py-3 text-right">Rate</th>
                          <th scope="col" className="px-4 py-3 text-right">Amount</th>
                          <th scope="col" className="w-[120px] px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((l) => {
                          const warn = ndtLineNeedsPrice(l);
                          return (
                            <tr key={l.id} data-testid="ndt-ticket-line" data-money={l.money} className={`border-t border-l-4 border-fieldiq-border ${warn ? 'border-l-fi-amber' : 'border-l-transparent'}`}>
                              <td className="px-4 py-3">
                                <p className="text-sm font-semibold text-white">{l.description}</p>
                                <p className="mt-0.5 text-xs text-fieldiq-muted">
                                  {ndtLineUnitLabel(l.unit)}
                                  {l.weldNumber ? ` · weld ${l.weldNumber}` : ''}
                                  {l.source === 'auto' ? ' · automatic' : ''}
                                  {l.workDate ? ` · ${ndtDay(l.workDate)}` : ''}
                                </p>
                                {warn && l.unpricedReason && (
                                  <p className="mt-1 flex items-start gap-1 text-xs text-fieldiq-text" data-testid="ndt-ticket-line-reason">
                                    <AlertTriangle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                                    {l.unpricedReason}
                                  </p>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{ndtQtyText(l.quantity, l.unit, l.distanceUnit)}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums text-fieldiq-text">
                                <p className="font-mono">{ndtRateText(l.rate, ticket.currency, l.unit, l.distanceUnit)}</p>
                                <p className="mt-0.5 text-xs text-fieldiq-muted">{rateSourceText(l)}</p>
                              </td>
                              <td className={`whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums ${warn ? 'font-bold text-fieldiq-text' : 'font-mono text-white'}`} data-testid="ndt-ticket-line-amount">
                                {ndtLineAmountText(l, ticket.currency)}
                              </td>
                              <td className="px-4 py-3">
                                {canWork && isDraft && (
                                  <div className="flex justify-end gap-1">
                                    {canPrice && warn && (
                                      <button
                                        type="button"
                                        className="inline-flex size-8 items-center justify-center rounded-fi-control text-fieldiq-muted transition hover:bg-fieldiq-dark/60 hover:text-fieldiq-text"
                                        aria-label={`Pick the rate for ${l.description}`}
                                        onClick={() => setLineDialog({ kind: 'pick', line: l })}
                                        data-testid="ndt-ticket-line-pick"
                                      >
                                        <Tag className="size-4" aria-hidden="true" />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      className="inline-flex size-8 items-center justify-center rounded-fi-control text-fieldiq-muted transition hover:bg-fieldiq-dark/60 hover:text-fieldiq-text"
                                      aria-label={`Edit ${l.description}`}
                                      onClick={() => setLineDialog({ kind: 'edit', line: l })}
                                      data-testid="ndt-ticket-line-edit"
                                    >
                                      <Pencil className="size-4" aria-hidden="true" />
                                    </button>
                                    <button
                                      type="button"
                                      className="inline-flex size-8 items-center justify-center rounded-fi-control text-fieldiq-muted transition hover:bg-fieldiq-dark/60 hover:text-fieldiq-text"
                                      aria-label={`Remove ${l.description}`}
                                      onClick={() => setLineDialog({ kind: 'remove', line: l })}
                                      data-testid="ndt-ticket-line-remove"
                                    >
                                      <Trash2 className="size-4" aria-hidden="true" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-l-4 border-fieldiq-border border-l-transparent">
                          <th scope="row" colSpan={3} className="px-4 py-3 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                            Subtotal (before tax)
                          </th>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-base font-extrabold tabular-nums text-white" data-testid="ndt-ticket-subtotal">
                            {ndtSubtotalText(ticket.subtotal, ticket.lineCount, ticket.currency)}
                          </td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </NdtPanel>

              {removed.length > 0 && (
                <section className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3" data-testid="ndt-ticket-removed">
                  <p className="text-[0.62rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Removed lines ({removed.length})</p>
                  <ul className="mt-2 space-y-1 text-sm text-fieldiq-text">
                    {removed.map((l) => (
                      <li key={l.id}>
                        <span className="line-through">{l.description}</span>
                        <span className="text-fieldiq-muted"> — {l.deleteReason || 'no reason recorded'}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
        </div>
      </section>

      {ticket && lineDialog && (
        <NdtDialog
          open
          title={
            lineDialog.kind === 'add'
              ? `Add a line to ${ticket.ticketNumber}`
              : lineDialog.kind === 'edit'
                ? 'Edit line'
                : lineDialog.kind === 'pick'
                  ? 'Pick the rate'
                  : 'Remove line'
          }
          onClose={() => setLineDialog(null)}
        >
          {lineDialog.kind === 'add' ? (
            <NdtAddLineForm
              ticketId={ticket.id}
              customerId={ticket.customerId}
              currency={ticket.currency}
              day={day}
              canPrice={canPrice}
              onDone={() => setLineDialog(null)}
              onCancel={() => setLineDialog(null)}
            />
          ) : lineDialog.kind === 'edit' ? (
            <NdtEditLineForm line={lineDialog.line} currency={ticket.currency} canPrice={canPrice} onDone={() => setLineDialog(null)} onCancel={() => setLineDialog(null)} />
          ) : lineDialog.kind === 'pick' ? (
            <NdtPickRateForm
              line={lineDialog.line}
              customerId={ticket.customerId}
              day={lineDialog.line.workDate ?? day}
              onDone={() => setLineDialog(null)}
              onCancel={() => setLineDialog(null)}
            />
          ) : (
            <NdtRemoveLineForm line={lineDialog.line} onDone={() => setLineDialog(null)} onCancel={() => setLineDialog(null)} />
          )}
        </NdtDialog>
      )}
    </div>
  );
}
